import { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { format } from 'date-fns';
import { Software } from 'models';
import Card from 'components/UI/Card';
import { ContentContainer, NameContainer, SubTitle } from './styled';
import SortableTable from 'components/UI/Table/SortableTable';
import ReleaseModal from '../ReleaseModal';
import Button from 'components/UI/Button';
import { CardHeaderTop, CardHeaderTopColumn } from 'pages/Software/styled';
import Heading, { Type as HeadingType } from 'components/UI/Heading';
import Loader from 'components/UI/Loader';
import { useAccess } from 'hooks/useAccess';

type Props = {
  software: Software | null;
  isLoading: boolean;
};

const headerCells = [
  {
    title: 'software.product_pnc',
    sortable: false,
  },
  {
    title: 'software.release_date',
    sortable: false,
  },
];

export type TableReleases = {
  pnc: string;
  releaseDate: string;
};

const ReleaseCard: FC<Props> = ({ software, isLoading }) => {
  const { createRolloutAccess } = useAccess();

  const [openReleaseSoftwareModal, setOpenReleaseSoftwareModal] =
    useState<boolean>(false);

  const onOpen = useCallback(() => {
    setOpenReleaseSoftwareModal(true);
  }, [setOpenReleaseSoftwareModal]);

  const onClose = useCallback(() => {
    setOpenReleaseSoftwareModal(false);
  }, [setOpenReleaseSoftwareModal]);

  // Details
  const releases = useMemo(() => {
    if (isLoading) {
      return <Loader center />;
    }
    if (!software) {
      return null;
    }
    const { releasedFor } = software.attributes;
    if (!releasedFor) {
      return null;
    }
    const table: TableReleases[] = releasedFor.map(
      (releasedFor: { pnc: string; releaseDate: number }) => {
        const { pnc, releaseDate } = releasedFor;
        const release = {
          pnc: pnc || '–',
          releaseDate: releaseDate
            ? format(Number(releaseDate), 'yyyy-MM-dd HH:mm:ss')
            : '-',
        };
        return release;
      }
    );
    return <SortableTable headerCells={headerCells} bodyRows={table} />;
  }, [software, isLoading]);

  return (
    <Card>
      <CardHeaderTop>
        <CardHeaderTopColumn>
          <Heading type={HeadingType.h3} uppercase>
            <FormattedMessage id="software.releases" />
          </Heading>
        </CardHeaderTopColumn>
        <CardHeaderTopColumn>
          {createRolloutAccess && (
            <Button
              id="open_create_release_btn"
              backgroundColor="transparent"
              color="primary"
              onClick={onOpen}
            >
              <FormattedMessage id="software_release.btn_release" />
            </Button>
          )}
        </CardHeaderTopColumn>
      </CardHeaderTop>
      <SubTitle>
        <FormattedMessage id="software.access" />
      </SubTitle>
      <ContentContainer>
        <NameContainer>{releases}</NameContainer>
      </ContentContainer>
      <ReleaseModal
        open={openReleaseSoftwareModal}
        onClose={onClose}
        softwareName={`${software?.attributes.name} ${software?.attributes.version}`}
      />
    </Card>
  );
};

export default ReleaseCard;
